
import { useState, useCallback } from 'react';
import { MessageType } from '@/types/assistant';
import { getFeedbackForMessages } from '@/services/ai-feedback-service';
import { supabase } from '@/lib/supabase';

const MESSAGES_PER_PAGE = 15;

export const useMessageInteractions = ({
  currentSessionId,
  userId,
  messages,
  setMessages,
  onMessagesChange
}: {
  currentSessionId: string | null;
  userId: string | null;
  messages: MessageType[];
  setMessages: React.Dispatch<React.SetStateAction<MessageType[]>>;
  onMessagesChange?: (messagesUpdater: MessageType[] | ((prev: MessageType[]) => MessageType[])) => void;
}) => {
  const [feedback, setFeedback] = useState<{ [messageId: string]: 'positive' | 'negative' | null }>({});
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [isUpdatingFeedback, setIsUpdatingFeedback] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [totalMessageCount, setTotalMessageCount] = useState(0);

  const handleFeedbackChange = useCallback((messageId: string, newFeedback: 'positive' | 'negative' | null) => {
    setFeedback(prevFeedback => ({
      ...prevFeedback,
      [messageId]: newFeedback,
    }));
  }, []);

  const loadFeedbackForMessages = useCallback(async (messages: MessageType[], userId: string): Promise<MessageType[]> => {
    if (!messages.length || !userId) return messages;

    try {
      const messageIds = messages.map(msg => msg.id);
      const feedbackMap = await getFeedbackForMessages(messageIds, userId);

      return messages.map(msg => ({
        ...msg,
        feedback: feedbackMap[msg.id] || null,
        is_streaming: false, // Messages from database are never streaming
      }));
    } catch (error) {
      console.error('Error loading feedback for messages:', error);
      return messages.map(msg => ({
        ...msg,
        is_streaming: false, // Ensure is_streaming is always boolean
      }));
    }
  }, []);

  const updateMessageFeedback = useCallback(async (messageId: string, feedback: 'positive' | 'negative' | null): Promise<boolean> => {
    if (!userId || !currentSessionId) {
      console.error('Cannot update feedback: missing userId or sessionId');
      return false;
    }

    setIsUpdatingFeedback(true);
    
    try {
      // Update feedback in database
      const { error } = await supabase
        .from('message_feedback')
        .upsert({
          message_id: messageId,
          user_id: userId,
          feedback_type: feedback,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error updating message feedback:', error);
        return false;
      }

      // Update local state
      handleFeedbackChange(messageId, feedback);
      
      // Update messages in state
      const updatedMessages = messages.map(msg => 
        msg.id === messageId ? { ...msg, feedback } : msg
      );
      
      if (onMessagesChange) {
        onMessagesChange(updatedMessages);
      } else {
        setMessages(updatedMessages);
      }

      return true;
    } catch (error) {
      console.error('Error updating feedback:', error);
      return false;
    } finally {
      setIsUpdatingFeedback(false);
    }
  }, [userId, currentSessionId, messages, handleFeedbackChange, onMessagesChange, setMessages]);

  const loadMoreMessages = useCallback(async (): Promise<boolean> => {
    if (!currentSessionId || !userId || isLoadingMoreMessages || !hasMoreMessages) {
      return false;
    }

    setIsLoadingMoreMessages(true);
    
    try {
      const offset = messages.length;
      
      const { data: messagesData, error } = await supabase
        .from('assistant_messages')
        .select('*')
        .eq('session_id', currentSessionId)
        .order('created_at', { ascending: false })
        .range(offset, offset + MESSAGES_PER_PAGE - 1);

      if (error) {
        console.error('Error loading more messages:', error);
        return false;
      }

      if (!messagesData || messagesData.length === 0) {
        setHasMoreMessages(false);
        return false;
      }

      const formattedMessages: MessageType[] = messagesData.map(msg => ({
        id: msg.id,
        content: msg.content,
        role: msg.role,
        timestamp: new Date(msg.created_at),
        session_id: msg.session_id,
        user_id: msg.user_id,
        suggestions: msg.suggestions || [],
        feedback: null,
        is_streaming: false, // Messages from database are never streaming
      }));

      // Load feedback for new messages
      const messagesWithFeedback = await loadFeedbackForMessages(formattedMessages, userId);
      
      // Prepend new messages (they're in reverse chronological order from DB)
      const updatedMessages = [...messagesWithFeedback.reverse(), ...messages];
      
      if (onMessagesChange) {
        onMessagesChange(updatedMessages);
      } else {
        setMessages(updatedMessages);
      }

      // Check if we have more messages
      if (messagesData.length < MESSAGES_PER_PAGE) {
        setHasMoreMessages(false);
      }

      return true;
    } catch (error) {
      console.error('Error in loadMoreMessages:', error);
      return false;
    } finally {
      setIsLoadingMoreMessages(false);
    }
  }, [currentSessionId, userId, isLoadingMoreMessages, hasMoreMessages, messages, loadFeedbackForMessages, onMessagesChange, setMessages]);

  const resetPagination = useCallback(() => {
    setHasMoreMessages(true);
    setTotalMessageCount(0);
  }, []);

  const getMessageCount = useCallback(() => {
    return messages.length;
  }, [messages.length]);

  return {
    feedback,
    handleFeedbackChange,
    loadFeedbackForMessages,
    updateMessageFeedback,
    loadMoreMessages,
    resetPagination,
    setTotalMessageCount,
    getMessageCount,
    hasMoreMessages,
    isLoadingMoreMessages,
    isUpdatingFeedback
  };
};
